// Requirement 20 in the app: the commercial right to a premium module comes from the signed licence (verified offline,
// account-service.ts) through the one entitlement map (@vero/platform entitlements.ts). Without the right a module is
// never hidden and its records are never touched: the screen opens read-only under an «available in plan X / add-on Y»
// card (store builds: no plan or purchase wording, PURCHASE_UI). The write guard of the workspace refuses the module's
// changes (account-service.ts → ws.writeGuard). Nothing is switched on or bought from here — «See plans» only opens the
// Account screen, where buying is a separate, explicit step.
import { useEffect, useState, type ReactNode } from 'react';
import { ENTITLEMENTS, PLAN_NAMES, featureAccess, featureOfModule, type Feature, type FeatureAccess, type RightsContext } from '@vero/platform';
import { accountService, type AccountUiState } from './account-service.ts';
import { useRuntime } from './runtime.tsx';
import { navigate } from './router.ts';
import { Icon } from './icons.tsx';
import { PURCHASE_UI } from './edition.ts';
import { lang3 } from '../guide/l3.ts';

export const rightsOf = (a: AccountUiState | null): RightsContext => ({ licence: a?.licence ?? null, state: a?.licenceState ?? 'none', enforced: !!a?.enforced });

export function useRights(): RightsContext {
  const [a, setA] = useState<AccountUiState | null>(() => accountService()?.snapshot() ?? null);
  useEffect(() => accountService()?.subscribe(setA), []);
  return rightsOf(a);
}
export function useFeature(f: Feature): FeatureAccess { return featureAccess(f, useRights()); }

/** «available in Professional, Team / Agency» — or, in a store build, only that it is not part of the current subscription */
export function UpgradeCard({ access }: { access: FeatureAccess }) {
  const rt = useRuntime();
  const { t } = rt;
  const label = ENTITLEMENTS[access.feature].label[lang3(rt.locale)];
  const lapsed = access.reason === 'lapsed';
  const where = [access.plans.length ? t('rights.in_plans', { plans: access.plans.map((p) => PLAN_NAMES[p]).join(', ') }) : '', access.addons.length ? t('rights.in_addons', { addons: access.addons.map((x) => { const k = `rights.addon.${x}`; const v = t(k); return v === k ? x : v; }).join(', ') }) : ''].filter(Boolean).join(' · ');
  return (
    <div className="callout warn upgrade-card" role="status" data-testid={`upgrade-${access.feature}`} data-reason={access.reason}>
      <Icon name="lock" size={16} />
      <div style={{ flex: 1 }}>
        <b>{lapsed ? t('rights.lapsed_title', { feature: label }) : t('rights.title', { feature: label })}</b>
        <div className="small">{lapsed ? t('rights.lapsed_body') : t('rights.body')}</div>
        {!lapsed && (PURCHASE_UI ? where && <div className="small" data-testid="upgrade-where">{where}</div> : <div className="small muted" data-testid="upgrade-store">{t('rights.store')}</div>)}
      </div>
      {PURCHASE_UI && <button className="btn btn-sm" onClick={() => navigate('/account')} data-testid="upgrade-see-plans">{t('web.see_plans')}</button>}
    </div>
  );
}

/** wraps a module's screen: full access → the screen; otherwise the card, then the screen read-only (a derived screen: the card only) */
export function ModuleGate({ moduleId, children }: { moduleId: string; children: ReactNode }) {
  const f = featureOfModule(moduleId);
  const rights = useRights();
  // a server-enforced module (website inbox, portal, AI drafts) asks the platform and shows its answer itself
  if (!f || ENTITLEMENTS[f].enforced === 'server') return <>{children}</>;
  const acc = featureAccess(f, rights);
  if (acc.access === 'full') return <>{children}</>;
  return <>
    <UpgradeCard access={acc} />
    {!ENTITLEMENTS[f].derived && <div className="module-readonly" data-testid="module-readonly">{children}</div>}
  </>;
}
